import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'

export default function CompleteBuild() {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState([])

  // Data
  const [configurations, setConfigurations] = useState([])
  const [allOptions, setAllOptions] = useState([])
  const [allParts, setAllParts] = useState([])

  // Form state
  const [selectedConfigId, setSelectedConfigId] = useState('')
  const [selectedOptionIds, setSelectedOptionIds] = useState([])
  const [vanSerialNumber, setVanSerialNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [completedAt, setCompletedAt] = useState(new Date().toISOString().split('T')[0])

  // Parts with editable quantities. Each part carries `custom: true` when it was
  // manually added to this build (i.e. not derived from the configuration/options),
  // so we can preserve those rows when the configuration preview reloads.
  const [parts, setParts] = useState([])
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [partToAdd, setPartToAdd] = useState('')

  useEffect(() => {
    Promise.all([
      fetch('/api/configurations').then(r => r.json()),
      fetch('/api/options').then(r => r.json()),
      fetch('/api/parts').then(r => r.json())
    ])
      .then(([configs, opts, prts]) => {
        setConfigurations(configs)
        setAllOptions(opts)
        setAllParts(prts)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error loading data:', err)
        setLoading(false)
      })
  }, [])

  // Load preview when configuration or options change
  useEffect(() => {
    if (!selectedConfigId) {
      setParts([])
      return
    }

    setLoadingPreview(true)
    fetch('/api/builds/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        configuration_id: selectedConfigId,
        option_ids: selectedOptionIds
      })
    })
      .then(r => r.json())
      .then(data => {
        // Transform preview data into editable parts array
        const previewParts = data.parts.map(part => ({
          part_id: part.part_id,
          description: part.description,
          price: part.price,
          quantity: part.quantity,
          current_stock: part.current_stock,
          custom: false
        }))

        // Preserve any manually-added parts across preview reloads. If a preserved
        // custom part is now also supplied by the configuration/options, fold its
        // quantity into the preview row instead of duplicating the part.
        setParts(prev => {
          const customParts = prev.filter(p => p.custom)
          const merged = [...previewParts]
          customParts.forEach(cp => {
            const existing = merged.find(p => p.part_id === cp.part_id)
            if (existing) {
              existing.quantity += cp.quantity
            } else {
              merged.push(cp)
            }
          })
          return merged
        })
        setLoadingPreview(false)
      })
      .catch(err => {
        console.error('Error loading preview:', err)
        setLoadingPreview(false)
      })
  }, [selectedConfigId, selectedOptionIds])

  const toggleOption = (optionId) => {
    if (selectedOptionIds.includes(optionId)) {
      setSelectedOptionIds(selectedOptionIds.filter(id => id !== optionId))
    } else {
      setSelectedOptionIds([...selectedOptionIds, optionId])
    }
  }

  const updatePartQuantity = (partId, newQuantity) => {
    const qty = parseInt(newQuantity, 10)
    if (isNaN(qty) || qty < 0) return

    setParts(prev => prev.map(part =>
      part.part_id === partId
        ? { ...part, quantity: qty }
        : part
    ))
  }

  const addCustomPart = () => {
    const partId = parseInt(partToAdd, 10)
    if (!partId) return

    const part = allParts.find(p => p.id === partId)
    if (!part) return

    setParts(prev => {
      // If this part is already in the build, just bump its quantity by one
      // rather than adding a duplicate row (the backend keys parts by id).
      const existing = prev.find(p => p.part_id === partId)
      if (existing) {
        return prev.map(p =>
          p.part_id === partId ? { ...p, quantity: p.quantity + 1 } : p
        )
      }
      // Otherwise add a new custom row at the top of the table.
      return [
        {
          part_id: part.id,
          description: part.description,
          price: part.price,
          quantity: 1,
          current_stock: part.quantity_in_stock,
          custom: true
        },
        ...prev
      ]
    })
    setPartToAdd('')
  }

  const removePart = (partId) => {
    setParts(prev => prev.filter(p => p.part_id !== partId))
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  // Calculate totals from current parts state
  const calculateTotalCost = () => {
    return parts.reduce((sum, part) => sum + (part.price * part.quantity), 0)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrors([])
    setSubmitting(true)

    // Build custom parts array for submission
    const customParts = parts
      .filter(p => p.quantity > 0)
      .map(p => ({
        part_id: p.part_id,
        quantity: p.quantity
      }))

    fetch('/api/builds', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        build: {
          configuration_id: selectedConfigId,
          option_ids: selectedOptionIds,
          van_serial_number: vanSerialNumber,
          notes: notes,
          completed_at: completedAt,
          custom_parts: customParts
        }
      })
    })
      .then(r => r.json())
      .then(data => {
        if (data.errors) {
          setErrors(data.errors)
          setSubmitting(false)
        } else {
          navigate('/builds')
        }
      })
      .catch(err => {
        console.error('Error creating build:', err)
        setErrors(['An unexpected error occurred'])
        setSubmitting(false)
      })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0 max-w-4xl mx-auto">
      <div className="mb-6">
        <Link to="/inventory" className="text-gray-600 hover:text-gray-900">
          &larr; Back to Inventory
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Complete a Build</h1>
        <p className="text-gray-600 mb-6">
          Select a configuration and any options to record the parts used in this build.
        </p>

        {errors.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <ul className="list-disc list-inside text-red-700">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Step 1: Select Configuration */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">1. Select Configuration</h2>
            {configurations.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-yellow-800">
                  No configurations found. <Link to="/configurations/new" className="underline">Create one first</Link>.
                </p>
              </div>
            ) : (
              <select
                value={selectedConfigId}
                onChange={(e) => setSelectedConfigId(e.target.value)}
                required
                className="w-full md:w-1/2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">Select a configuration...</option>
                {configurations.map(config => (
                  <option key={config.id} value={config.id}>
                    {config.name} ({config.parts_count} parts, {formatCurrency(config.total_cost)})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Step 2: Select Options */}
          {selectedConfigId && (
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">2. Select Options (Optional)</h2>
              {allOptions.length === 0 ? (
                <p className="text-gray-500 text-sm">No options available</p>
              ) : (
                <div className="space-y-2">
                  {allOptions.map(option => (
                    <label
                      key={option.id}
                      className={`flex items-center p-3 rounded-md cursor-pointer ${
                        selectedOptionIds.includes(option.id)
                          ? 'bg-gray-100 border-2 border-gray-900'
                          : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedOptionIds.includes(option.id)}
                        onChange={() => toggleOption(option.id)}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {option.name}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        ({option.parts_count} part modification{option.parts_count !== 1 ? 's' : ''})
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Parts Preview - Now Editable */}
          {selectedConfigId && (
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-2">3. Parts to be Used</h2>
              <p className="text-sm text-gray-500 mb-4">
                Quantities are pre-filled from your selections. You can adjust them, add
                extra parts specific to this build, or remove parts as needed. Changes here
                only affect this build&mdash;they do not modify the configuration.
              </p>

              {/* Add an arbitrary part to this build */}
              <div className="flex items-end gap-3 mb-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
                    Add a part to this build
                  </label>
                  <select
                    value={partToAdd}
                    onChange={(e) => setPartToAdd(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  >
                    <option value="">Select a part to add...</option>
                    {allParts.map(part => (
                      <option key={part.id} value={part.id}>
                        {part.description} ({formatCurrency(part.price)})
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={addCustomPart}
                  disabled={!partToAdd}
                  className={`px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                    partToAdd
                      ? 'text-gray-700 bg-white hover:bg-gray-50'
                      : 'text-gray-400 bg-gray-50 cursor-not-allowed'
                  }`}
                >
                  + Add Part
                </button>
              </div>

              {loadingPreview ? (
                <p className="text-gray-500">Loading preview...</p>
              ) : parts.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty Used</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Current Stock</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">After Build</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {parts.map((part) => {
                        const stockAfter = part.current_stock - part.quantity
                        const lineCost = part.price * part.quantity
                        return (
                          <tr key={part.part_id} className={stockAfter < 0 ? 'bg-red-50' : ''}>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {part.description}
                              {part.custom && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  Added
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="number"
                                min="0"
                                value={part.quantity}
                                onChange={(e) => updatePartQuantity(part.part_id, e.target.value)}
                                className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-gray-900"
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">{part.current_stock}</td>
                            <td className={`px-4 py-2 text-sm font-medium ${
                              stockAfter < 0 ? 'text-red-600' : 'text-gray-900'
                            }`}>
                              {stockAfter}
                              {stockAfter < 0 && (
                                <span className="ml-1 text-xs">(negative!)</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(lineCost)}</td>
                            <td className="px-4 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => removePart(part.part_id)}
                                className="text-red-600 hover:text-red-900 text-sm"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="4" className="px-4 py-2 text-sm font-bold text-gray-900 text-right">
                          Total Cost:
                        </td>
                        <td className="px-4 py-2 text-sm font-bold text-gray-900">
                          {formatCurrency(calculateTotalCost())}
                        </td>
                        <td className="px-4 py-2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : null}
            </div>
          )}

          {/* Step 4: Additional Info */}
          {selectedConfigId && (
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">4. Additional Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Van Serial Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={vanSerialNumber}
                    onChange={(e) => setVanSerialNumber(e.target.value)}
                    placeholder="e.g., WV-001"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If this matches a van in inventory, it will be linked automatically
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Completion Date
                  </label>
                  <input
                    type="date"
                    value={completedAt}
                    onChange={(e) => setCompletedAt(e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Any additional notes about this build..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end space-x-4 border-t pt-6">
            <Link
              to="/inventory"
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={!selectedConfigId || submitting}
              className={`px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                !selectedConfigId || submitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {submitting ? 'Submitting...' : 'Complete Build'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
